/**
 * The personal delivery record (v2, "The record"; build plan decision 7): what a person committed to,
 * what happened, and their own words beside each line. Derived, never stored: the commitments and
 * their audit events are the facts, and this module reads an outcome off them so the same line means
 * the same thing on the web, in a brief and in a 1:1.
 *
 * Who sees it is hard rule 3: the person and their manager, nobody else. The manager sees the outcome
 * and the note, never the source text (hard rule 2), never a candidate or a set-aside item. There is
 * no score here and nothing to rank: four counts, each of which is a plain count of lines below it.
 */
import type { Audit, Commitment, CommitmentKind, CommitmentStatus, Iso, PersonId, ProgramId, SourceKind } from "./commitment.js";
import { addCalendarDays, dateOf, daysBetween, fmtDay, fmtDuration, parseDuration, plural } from "./dates.js";
import { attentionOf, type Attention, type SourceThread } from "./attention.js";
import { compareQueue, splitCadences, takeRanked, WEB_NON_CADENCE } from "./queue.js";
import { thresholdMs, unattended } from "./unattended.js";
import { settlementIn, type Settlement } from "./resemblance.js";

export type OutcomeTone = "good" | "watch" | "bad" | "neutral";

export type OutcomeKind =
  | "candidate" | "open" | "overdue" | "moved-open"
  | "closed-on-date" | "closed-early" | "closed-late" | "moved-closed"
  | "transferred" | "transfer-pending" | "transfer-offered" | "delegated" | "set-aside" | "withdrawn";

export interface Outcome {
  kind: OutcomeKind;
  label: string;
  tone: OutcomeTone;
  /** A second line under the label: the acknowledgement clock for an ask, who is doing a delegated item. */
  detail?: string;
}

/**
 * What a person may do to a line of their own record. `confirm`/`set-aside` on a candidate; `accept`,
 * `decline`, `move` on a proposal (hard rule 5a); `accept-transfer`/`decline-transfer` on a transfer
 * offered to them; `transfer`, `delegate`, `close`, `move`, `note`, `set-aside` on an open item.
 */
export type RecordAction =
  | "confirm" | "set-aside" | "restore" | "close" | "move" | "note"
  | "accept" | "decline" | "transfer" | "accept-transfer" | "decline-transfer" | "delegate"
  | "assign" | "reassign" | "retitle";

/** `byRole` is the role the author held at the time; `byName` is resolved for display. */
export interface RecordNote { at: Iso; by: PersonId; text: string; byRole?: string; byName?: string }

export interface RecordLine {
  id: string;
  programId: ProgramId;
  programName?: string;
  kind: CommitmentKind;
  status: CommitmentStatus;
  title: string;
  cadenceId?: string;
  vipRelation?: string;
  /** The date the line is filed under: the close date once closed, the due date while open. */
  when: string;
  dueDate: string;
  /** False when Trellis assumed the date. The rank puts an assumed date last (queue.ts `dueBucket`). */
  dueDateStated?: boolean;
  originalDueDate?: string;
  closedAt?: Iso;
  moves: number;
  source: { kind: SourceKind; at: Iso; label: string; excerpt?: string; speaker?: string; /** Where to open it: the email, the meeting, the contract, the program. Owner only, like the excerpt. */ url?: string };
  outcome: Outcome;
  notes: RecordNote[];
  link?: { system: string; ref: string };
  /** What the viewer may do to this line. Only ever non-empty for the owner. */
  actions: RecordAction[];
  /** Who asked, when somebody did: shown on the line, not buried in the source. */
  askedBy?: string;
  /** The initiative this line is labelled with, if any, and whether it is one of its milestones. */
  initiativeId?: string;
  milestone?: boolean;
  /** The unit the line is classified onto. A candidate has none: nothing is placed until it is confirmed. */
  unitId?: string;
  unitName?: string;
  /** The contract line that funds the unit, walked up the tree. Absent means nobody can say it is funded. */
  clin?: ClinTag;
  /** Why this is worth looking at first. Candidates only, and only on the owner's own record. */
  attention?: Attention;
  /**
   * Another line on this record this one might repeat. Shown rather than acted on: the owner decides,
   * because two obligations can be worded almost identically. `title` is absent when the other line is
   * not one this viewer can see. `settled` says a person compared the pair and answered: `repeats`
   * states the resemblance instead of wondering about it, and `different` says the two were compared
   * and kept apart, which is why the line still names the pair after the pointer has gone.
   */
  duplicateOf?: { id: string; title?: string; settled?: Settlement };
  /**
   * The other side of the same resemblance: lines on this record that say they repeat this one. Without
   * it somebody reading the original never learns a second row points at it, and the only way to find
   * the pair was to open the row that repeats. `settled` is only ever `repeats` here, because a pair
   * settled the other way keeps no pointer to be listed by.
   */
  repeatedBy?: { id: string; title: string; settled?: "repeats" }[];
}

/** A contract line as a row shows it: the number, and which of the two line hues draws it. */
export interface ClinTag { key: string; tone: "a" | "b" }

/** A unit as the record needs one: enough to walk up to its contract line and name it. */
export interface RecordUnit {
  id: string;
  programId: ProgramId;
  kind: string;
  name: string;
  /** The line number on a CLIN ("0001"); the slug on anything else. */
  key: string;
  parentId: string | null;
  active?: boolean;
}

export interface RecordCounts {
  closed: number;
  closedOnDate: number;
  movedOrSlipped: number;
  asksUnacknowledgedPastStandard: number;
}

export interface RecordRole { programId: ProgramId; programName?: string; roleId: string; roleName: string }

export interface RecordView {
  owner: PersonId;
  ownerName: string;
  viewer: PersonId;
  viewerIsOwner: boolean;
  asOf: Iso;
  days: number;
  /** The acknowledgement standard as a person would say it: "1 day". */
  ackStandard: string;
  roles: RecordRole[];
  counts: RecordCounts;
  /** Waiting for the owner to confirm, set aside or decline. Owner only; never sent to a manager. */
  candidates: RecordLine[];
  lines: RecordLine[];
  /** Ranked non-cadence working set (web cap 20). */
  working: RecordLine[];
  /** Cadence items, sibling of the working set, not capped. */
  cadences: RecordLine[];
  /** Non-cadence live items not shown in `working`. */
  workingHeld: number;
  /** Closed lines older than the window, so the page can say "31 more since June". */
  earlier: number;
  /** The programs the owner is on (roster or role), for adding a commitment or proposing one. */
  programs: { id: ProgramId; name: string }[];
  /** What the owner is accountable for beside their own lines. Owner's own record only. */
  side?: RecordSide;
}

export type RecordResult = { forbidden: true; reason: string } | { forbidden: false; record: RecordView };

export interface RecordInput {
  owner: PersonId;
  /** The owner's commitments in any status, plus any they transferred away (found through the audit) and any offered to them by transfer. */
  commitments: Commitment[];
  /** Events for those commitments, and for the originals of any delegated copies among them. */
  events: Audit[];
  commentary?: (RecordNote & { commitmentId: string })[];
  people?: { upn: PersonId; displayName: string; managerUpn?: string | null }[];
  programs?: { id: ProgramId; name: string }[];
  /** Program ids the owner is on. When given, `RecordView.programs` is limited to these. */
  memberOf?: ProgramId[];
  /** The unit trees of those programs, so each line can name its unit and its contract line. */
  units?: RecordUnit[];
  roles?: { programId: ProgramId; roleId: string; upn: PersonId }[];
  roleNames?: Record<string, string>;
  /** Cadence ids to names, so a line reads "Cadence · Monthly report" rather than the id. */
  cadenceNames?: Record<string, string>;
  asOf?: Iso;
  days?: number;
  /** The model's `ack-within` parameter (ISO-8601 duration). Defaults to one day. */
  ackWithin?: string;
  /**
   * The thread each commitment was read from, keyed by `source.ref`: when it landed and who the
   * reader's own VIP list recognises on it. Whoever loads the record resolves the addresses, so this
   * module stays a pure function and stays testable without a database. Absent means the caller had
   * nothing to say, never that the thread was empty.
   */
  threads?: Record<string, SourceThread>;
}

/** How deep a unit tree may be before we stop walking: five levels, plus room for a bad row. */
const MAX_TREE_DEPTH = 12;

/**
 * The contract line a unit sits under, or undefined when it sits under none. The tree is
 * Program > CLIN > Workstream > Team > Sub-team, so the answer is the first CLIN ancestor;
 * a workstream parked on the holding unit has no CLIN and that is a finding, not a blank.
 *
 * The hue is the line's position among its programme's lines, so a number always draws in
 * the same colour on every screen of that contract. Two hues cycle: the number is set
 * beside the bar everywhere, so colour never carries the meaning on its own.
 */
export function clinIndex(units: RecordUnit[]): (unitId: string | undefined) => ClinTag | undefined {
  const byId = new Map(units.map((u) => [u.id, u]));
  const linesOf = new Map<ProgramId, string[]>();
  for (const u of units) {
    if (u.kind !== "clin") continue;
    linesOf.set(u.programId, [...(linesOf.get(u.programId) ?? []), u.key]);
  }
  for (const [programId, keys] of linesOf) linesOf.set(programId, [...new Set(keys)].sort());
  return (unitId) => {
    let cur = unitId ? byId.get(unitId) : undefined;
    for (let depth = 0; cur && depth < MAX_TREE_DEPTH; depth++) {
      if (cur.kind === "clin") {
        const at = (linesOf.get(cur.programId) ?? []).indexOf(cur.key);
        return { key: cur.key, tone: at % 2 === 1 ? "b" : "a" };
      }
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return undefined;
  };
}

/** One lookup against a tree that is read once. Use `clinIndex` when asking about many units. */
export function clinOf(units: RecordUnit[], unitId: string | undefined): ClinTag | undefined {
  return clinIndex(units)(unitId);
}

const SOURCE_LABEL: Record<SourceKind, string> = { meeting: "Meeting", mail: "Mail", ticket: "Ticket", page: "Page", chat: "Chat", model: "Model", contract: "Contract event", proposal: "Proposal", person: "You added it", manager: "Proposed" };

const times = (n: number) => (n === 1 ? "once" : n === 2 ? "twice" : `${n} times`);

/** The record as its owner sees it. Redaction for anyone else is `recordFor`'s job. */
export function deriveRecord(input: RecordInput): RecordView {
  const asOf = input.asOf ?? new Date().toISOString();
  const today = dateOf(asOf);
  const days = input.days ?? 90;
  const ackMs = parseDuration(input.ackWithin ?? "P1D");
  const ackStandard = fmtDuration(ackMs);
  const names = new Map((input.people ?? []).map((p) => [p.upn, p.displayName]));
  const nameOf = (upn: PersonId | undefined) => (upn ? names.get(upn) ?? upn : "");
  const programNames = new Map((input.programs ?? []).map((p) => [p.id, p.name]));
  const units = input.units ?? [];
  const unitNames = new Map(units.map((u) => [u.id, u.name]));
  const clinFor = clinIndex(units);
  const notesByCommitment = new Map<string, RecordNote[]>();
  for (const n of input.commentary ?? []) {
    const list = notesByCommitment.get(n.commitmentId) ?? [];
    // The author and the role they held are carried through, so a note reads
    // "Nishita Rao, delivery lead" rather than an address.
    list.push({ at: n.at, by: n.by, text: n.text, ...(n.byRole ? { byRole: n.byRole } : {}), ...(nameOf(n.by) ? { byName: nameOf(n.by) } : {}) });
    notesByCommitment.set(n.commitmentId, list);
  }
  const eventsByCommitment = new Map<string, Audit[]>();
  for (const e of [...input.events].sort((a, b) => a.at.localeCompare(b.at))) {
    const list = eventsByCommitment.get(e.commitmentId) ?? [];
    list.push(e);
    eventsByCommitment.set(e.commitmentId, list);
  }

  // Titles of everything this build can see, so a suspected duplicate can name what it resembles
  // rather than printing an id at somebody.
  const titles = new Map(input.commitments.map((c) => [c.id, c.title]));

  const candidates: RecordLine[] = [];
  const lines: RecordLine[] = [];
  let earlier = 0;

  for (const c of input.commitments) {
    const evs = eventsByCommitment.get(c.id) ?? [];
    const moves = evs.filter((e) => e.event.type === "moved").length;
    const closedEv = [...evs].reverse().find((e) => e.event.type === "closed");
    const closedAt = closedEv?.at ?? (c.status === "closed" ? c.updatedAt : undefined);
    const delegatedEv = evs.find((e) => e.event.type === "delegated");
    // The date the person first accepted. A candidate has none yet; its stated date stands in.
    const accepted = c.originalDueDate ?? c.dueDate;
    const notes = [...(notesByCommitment.get(c.id) ?? []), ...c.commentary].sort((a, b) => a.at.localeCompare(b.at));
    const resemblance = resemblanceOf(c, evs, titles);

    let sourceLabel = c.kind === "ask" && c.askedBy
      ? `Customer ask · ${c.askedBy.name} · ${c.askedBy.channel}`
      : c.askedBy
        ? `${c.askedBy.name} asked · ${SOURCE_LABEL[c.source.kind].toLowerCase()}`
        : c.source.kind === "manager"
          ? `${nameOf(c.proposedBy ?? c.source.ref)} proposed`
          : c.source.kind === "model" && c.cadenceId
            ? `Cadence · ${input.cadenceNames?.[c.cadenceId] ?? c.cadenceId}`
            : SOURCE_LABEL[c.source.kind];
    sourceLabel += ` · ${fmtDay(c.source.at)}`;
    // Said the way an assumed due date is said below: the date stands, and so does what it is worth.
    if (c.source.atStated === false) sourceLabel += " · date not stated";
    if (c.link) sourceLabel += ` · live from ${c.link.ref}`;
    if (c.dueDateStated === false) sourceLabel += " · date assumed";

    const base = {
      id: c.id, programId: c.programId, kind: c.kind, status: c.status, title: c.title,
      dueDate: c.dueDate, moves, notes, ...(c.dueDateStated === false ? { dueDateStated: false } : {}),
      ...(c.cadenceId ? { cadenceId: c.cadenceId } : {}), ...(c.vipRelation ? { vipRelation: c.vipRelation } : {}),
      // Only somebody who actually asked. A meeting speaker or an outbound sender is
      // usually the owner promising, and "Ola asked:" on your own promise is a lie.
      ...(c.askedBy ? { askedBy: c.askedBy.name } : {}),
      ...((c as { initiativeId?: string }).initiativeId ? { initiativeId: (c as { initiativeId?: string }).initiativeId! } : {}),
      ...((c as { milestone?: boolean }).milestone ? { milestone: true } : {}),
      ...(c.unitId ? { unitId: c.unitId } : {}),
      ...(resemblance ? { duplicateOf: resemblance } : {}),
      ...(c.unitId && unitNames.has(c.unitId) ? { unitName: unitNames.get(c.unitId)! } : {}),
      ...(clinFor(c.unitId) ? { clin: clinFor(c.unitId)! } : {}),
      source: { kind: c.source.kind, at: c.source.at, label: sourceLabel, ...(c.source.excerpt ? { excerpt: c.source.excerpt } : {}), ...(c.source.speaker ? { speaker: c.source.speaker } : {}), ...(c.source.url ? { url: c.source.url } : {}) },
      ...(programNames.has(c.programId) ? { programName: programNames.get(c.programId)! } : {}),
      ...(c.originalDueDate ? { originalDueDate: c.originalDueDate } : {}),
      ...(closedAt ? { closedAt } : {}),
      ...(c.link ? { link: c.link } : {}),
    };

    // Offered by transfer: someone else's open commitment, waiting for this person to take it or not.
    if (c.owner !== input.owner && c.pendingTransferTo === input.owner) {
      candidates.push({
        ...base, when: c.dueDate, actions: ["accept-transfer", "decline-transfer"],
        source: { kind: c.source.kind, at: c.source.at, label: `${SOURCE_LABEL[c.source.kind]} · ${fmtDay(c.source.at)}` }, // the source text is the owner's
        outcome: { kind: "transfer-offered", label: `Transfer from ${nameOf(c.owner)}, waiting for you`, tone: "watch" },
      });
      continue;
    }

    // Transferred away: the commitment now belongs to someone else, and the record says so plainly.
    if (c.owner !== input.owner) {
      const requested = evs.find((e) => e.event.type === "transfer-requested" && e.event.by === input.owner);
      const acceptedEv = requested ? evs.find((e) => e.event.type === "transfer-accepted" && e.at >= requested.at) : undefined;
      if (!requested || !acceptedEv || acceptedEv.event.type !== "transfer-accepted") continue;
      const sameDay = dateOf(requested.at) === dateOf(acceptedEv.at);
      if (daysBetween(acceptedEv.at, today) > days) { earlier++; continue; }
      lines.push({
        ...base, when: dateOf(acceptedEv.at), actions: [],
        source: { ...base.source, label: `${sourceLabel} · transferred to ${nameOf(acceptedEv.event.by)} ${fmtDay(requested.at)}, accepted ${sameDay ? "same day" : fmtDay(acceptedEv.at)}` },
        outcome: { kind: "transferred", label: "Transferred, not a slip", tone: "neutral" },
      });
      continue;
    }

    if (c.status === "declined" || c.status === "intake") continue; // never entered the record (hard rule 5a); intake is a program fact

    if (c.status === "candidate") {
      const from = c.delegatedFrom ? nameOf(delegatedBy(c, eventsByCommitment)) : "";
      const label = c.delegatedFrom
        ? `Delegated from ${from}, waiting for you`
        : c.proposedBy
          ? `Proposed by ${nameOf(c.proposedBy)}, waiting for you`
          : "Waiting for you to confirm";
      // A proposal is accepted, declined or moved (hard rule 5a); anything else extracted is confirmed or set aside.
      const actions: RecordAction[] = c.proposedBy ? ["accept", "decline", "move", ...(c.kind === "ask" ? ["reassign" as const] : [])] : ["confirm", "set-aside", "retitle"];
      const attention = attentionOf(c, input.threads?.[c.source.ref]);
      candidates.push({ ...base, when: c.dueDate, actions, outcome: { kind: "candidate", label, tone: "watch" }, ...(attention.score ? { attention } : {}) });
      continue;
    }

    if (c.status === "set-aside") {
      lines.push({ ...base, when: dateOf(c.updatedAt), actions: ["restore"], outcome: { kind: "set-aside", label: "Set aside", tone: "neutral" } });
      continue;
    }

    // A withdrawn cadence period stays on the record, with no buttons and nothing to answer: it is why
    // the item stopped being overdue, and hiding it would make the correction invisible to the person
    // it was made for (docs/2026-09-26-obligation-states.md).
    if (c.status === "withdrawn") {
      const withdrawn = evs.find((e) => e.event.type === "cadence-withdrawn");
      // Trellis taking its own unconfirmed candidate back is the other way into this status, and
      // it belongs nowhere on the record: the proposal was never shown to anybody -- an item that
      // has been carded is not eligible to be taken back -- so there is no correction to explain
      // and the cadence wording below would be a lie about what happened.
      if (!withdrawn) continue;
      const reason = withdrawn.event.type === "cadence-withdrawn" ? withdrawn.event.reason : undefined;
      lines.push({
        ...base, when: dateOf(withdrawn.at), actions: [],
        outcome: {
          kind: "withdrawn", label: "Withdrawn, not yours to run", tone: "neutral",
          detail: reason === "no-evidence-path" ? "The contract had no configured way to close this period." : "The rule did not apply to this unit in this period.",
        },
      });
      continue;
    }

    const doing = c.delegatedTo ? nameOf(delegatedEv?.event.type === "delegated" ? delegatedEv.event.to : c.delegatedTo.doing) : "";
    const accountable = c.delegatedFrom ? nameOf(delegatedBy(c, eventsByCommitment)) : "";
    if (c.delegatedTo && delegatedEv) base.source.label += ` · delegated to ${doing} ${fmtDay(delegatedEv.at)}`;
    if (c.delegatedFrom) base.source.label += ` · delegated from ${accountable}`;

    let outcome: Outcome;
    let when: string;
    let actions: RecordAction[];
    if (c.status === "closed") {
      const closeDate = dateOf(closedAt ?? c.updatedAt);
      if (daysBetween(closeDate, today) > days) { earlier++; continue; }
      when = closeDate;
      actions = ["note"];
      const late = daysBetween(accepted, closeDate);
      if (moves > 0) outcome = { kind: "moved-closed", label: `Moved ${times(moves)}, closed ${fmtDay(closeDate)}`, tone: "bad" };
      else if (late === 0) outcome = { kind: "closed-on-date", label: "Closed on date", tone: "good" };
      else if (late < 0) outcome = { kind: "closed-early", label: `Closed ${plural(-late, "day")} early`, tone: "good" };
      else outcome = { kind: "closed-late", label: `Closed ${plural(late, "day")} late`, tone: "watch" };
    } else {
      when = c.dueDate;
      // Transfer and delegate need the item to be open and not already on its way somewhere.
      actions = ["close", "move", "note", "set-aside", "retitle"];
      if (!c.pendingTransferTo) { actions.push("transfer"); if (!c.delegatedTo) actions.push("delegate"); }
      const until = daysBetween(today, c.dueDate);
      if (c.pendingTransferTo) outcome = { kind: "transfer-pending", label: `Transfer to ${nameOf(c.pendingTransferTo)} pending`, tone: "neutral" };
      else if (c.delegatedTo) outcome = { kind: "delegated", label: `Delegated to ${doing || "someone"}, you stay accountable`, tone: until < 0 ? "bad" : "watch", detail: dueWords(until) };
      else if (moves > 0) outcome = { kind: "moved-open", label: `Moved ${times(moves)}, ${dueWords(until)}`, tone: until < 0 ? "bad" : "watch" };
      else if (until < 0) outcome = { kind: "overdue", label: `Open, ${plural(-until, "day")} overdue`, tone: "bad" };
      else outcome = { kind: "open", label: `Open, ${dueWords(until)}`, tone: "watch" };
      if (c.delegatedFrom) outcome.detail = `Delegated from ${accountable || "someone"}`;
    }

    if (c.kind === "ask") {
      const clock = ackClock(c, asOf, ackMs);
      outcome.detail = clock.acknowledged
        ? `Acknowledged in ${fmtDuration(clock.ms)} · standard is ${ackStandard}`
        : `Not yet acknowledged, ${fmtDuration(clock.ms)} · standard is ${ackStandard}`;
      if (clock.pastStandard) outcome.tone = "bad";
    }

    lines.push({ ...base, when, actions, outcome });
  }

  attachPointers([...candidates, ...lines]);

  lines.sort((a, b) => b.when.localeCompare(a.when) || a.title.localeCompare(b.title));
  // The same rank the working set uses, so the box on the record and the list behind it read in one
  // order. A transfer offered to this person carries no attention: the thread behind it is its
  // owner's.
  candidates.sort((a, b) => compareQueue({ ...a, createdAt: a.source.at }, { ...b, createdAt: b.source.at }, today));

  const closedLines = lines.filter((l) => l.status === "closed");
  const counts: RecordCounts = {
    closed: closedLines.length,
    closedOnDate: closedLines.filter((l) => l.outcome.kind === "closed-on-date" || l.outcome.kind === "closed-early").length,
    movedOrSlipped: lines.filter((l) => l.moves > 0 || l.outcome.kind === "closed-late" || l.outcome.kind === "overdue").length,
    asksUnacknowledgedPastStandard: lines.filter((l) => l.kind === "ask" && (l.status === "open" || l.status === "closed")).filter((l) => {
      const c = input.commitments.find((x) => x.id === l.id)!;
      return ackClock(c, asOf, ackMs).pastStandard;
    }).length,
  };

  const roles: RecordRole[] = (input.roles ?? []).filter((r) => r.upn === input.owner).map((r) => ({
    programId: r.programId, roleId: r.roleId, roleName: input.roleNames?.[r.roleId] ?? r.roleId,
    ...(programNames.has(r.programId) ? { programName: programNames.get(r.programId)! } : {}),
  }));

  const programs = (input.programs ?? []).filter((p) => !input.memberOf || input.memberOf.includes(p.id)).map((p) => ({ id: p.id, name: p.name })).sort((a, b) => a.name.localeCompare(b.name));

  return attachWorkingSet({ owner: input.owner, ownerName: nameOf(input.owner), viewer: input.owner, viewerIsOwner: true, asOf, days, ackStandard, roles, counts, candidates, lines, working: [], cadences: [], workingHeld: 0, earlier, programs });
}

function attachWorkingSet(record: RecordView): RecordView {
  const today = dateOf(record.asOf);
  const live = [...record.candidates, ...record.lines.filter((l) => l.status === "open")];
  const { work, cadences } = splitCadences(live);
  const ranked = takeRanked(work.map((l) => ({ ...l, createdAt: l.source.at })), WEB_NON_CADENCE, today);
  const byId = new Map(live.map((l) => [l.id, l]));
  return {
    ...record,
    working: ranked.shown.map((s) => byId.get(s.id)!),
    cadences,
    workingHeld: ranked.held,
  };
}

/**
 * The record as `viewer` may see it. The owner sees everything; the owner's manager sees confirmed lines
 * with notes but no source text, no candidates, no set-asides and no buttons; anyone else is refused.
 */
export function recordFor(viewer: PersonId, owner: PersonId, input: Omit<RecordInput, "owner">): RecordResult {
  const full = deriveRecord({ ...input, owner });
  if (viewer === owner) return { forbidden: false, record: full };
  const person = (input.people ?? []).find((p) => p.upn === owner);
  if (!person || person.managerUpn !== viewer) return { forbidden: true, reason: "the record is visible to its owner and their manager only (hard rule 3)" };
  // The resemblance goes with the candidates and the source text. It is the owner's comparison to make
  // and a manager has no control for it, and both sides of it name another line by its title, which on
  // a manager's record would be a candidate's or a set-aside's words reaching somebody who is shown
  // neither (hard rules 2 and 3).
  const lines = full.lines
    .filter((l) => l.status === "open" || l.status === "closed")
    .map(({ duplicateOf: _d, repeatedBy: _r, ...l }) => ({ ...l, actions: [] as RecordAction[], source: { kind: l.source.kind, at: l.source.at, label: l.source.label } }));
  return { forbidden: false, record: attachWorkingSet({ ...full, viewer, viewerIsOwner: false, candidates: [], lines }) };
}

function dueWords(until: number): string {
  if (until === 0) return "due today";
  if (until < 0) return `${plural(-until, "day")} overdue`;
  return `due in ${plural(until, "day")}`;
}

/**
 * Who delegated a copy. The copy carries the original's id; the `delegated` event sits on the original,
 * so it is looked up there when the stored copy does not name the accountable person.
 */
function delegatedBy(c: Commitment, events: Map<string, Audit[]>): PersonId | undefined {
  if (c.delegatedFrom?.accountable) return c.delegatedFrom.accountable;
  if (!c.delegatedFrom) return undefined;
  const e = (events.get(c.delegatedFrom.commitmentId) ?? []).find((x) => x.event.type === "delegated" && x.event.linkedCommitmentId === c.id);
  return e?.event.type === "delegated" ? e.event.by : undefined;
}

/**
 * Fills in each line's `repeatedBy`: the other lines saying they repeat it.
 *
 * Built from the lines the view actually renders, not from every row that was read. A row the
 * derivation skipped has no page for the reader to open, and there are several ways to be skipped: a
 * status no record draws, a close or a transfer older than the window, a delegate's copy. Naming one
 * of those on the survivor would put work on the record that the record does not otherwise show.
 *
 * A pair somebody has pulled apart is not listed either: its pointer is gone, so there is nothing
 * pointing at the survivor any more, and its line says so on its own side.
 */
function attachPointers(shown: RecordLine[]): void {
  const byId = new Map(shown.map((l) => [l.id, l]));
  const lists = new Map<string, NonNullable<RecordLine["repeatedBy"]>>();
  for (const l of shown) {
    const of = l.duplicateOf?.id;
    if (!of || l.duplicateOf?.settled === "different" || !byId.has(of)) continue;
    const list = lists.get(of) ?? [];
    list.push({ id: l.id, title: l.title, ...(l.duplicateOf?.settled === "repeats" ? { settled: "repeats" as const } : {}) });
    lists.set(of, list);
  }
  for (const [id, list] of lists) {
    list.sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
    byId.get(id)!.repeatedBy = list;
  }
}

/**
 * What a line says about the item it might repeat, and whether anybody has answered.
 *
 * The column alone cannot say whether a person decided: `skill-runner.ts` writes it on a suspicion and
 * settling writes it on a judgement, so the events are read too. The survivor has to match, or a
 * judgement about an earlier pairing would speak for a later one.
 *
 * The pointer usually names the pair. It is taken off once somebody has said the two are different,
 * and the event that says so is then the only thing that knows which two they were, which is why the
 * line can still name it. A record that quietly dropped the sentence instead would invite the next
 * reader to compare the same pair again.
 */
function resemblanceOf(c: Commitment, events: Audit[], titles: Map<string, string>): RecordLine["duplicateOf"] {
  const judged = settlementIn(events.map((e) => e.event));
  const of = c.duplicateOf ?? (judged?.settled === "different" ? judged.of : undefined);
  if (!of) return undefined;
  return {
    id: of,
    ...(titles.has(of) ? { title: titles.get(of)! } : {}),
    ...(judged?.of === of ? { settled: judged.settled } : {}),
  };
}

/** How long the customer waited, and whether that beat the standard. Unacknowledged asks are still waiting. */
export function ackClock(c: Commitment, asOf: Iso, ackMs: number): { acknowledged: boolean; ms: number; pastStandard: boolean } {
  const start = Date.parse(c.source.at) || Date.parse(c.createdAt);
  const end = c.acknowledgedAt ? Date.parse(c.acknowledgedAt) : Date.parse(asOf);
  const ms = Math.max(0, end - start);
  return { acknowledged: !!c.acknowledgedAt, ms, pastStandard: ms > ackMs };
}

/** One slice of the outcome bar on a record: how many, what it means and what colour says so. */
export interface OutcomeSegment { key: string; n: number; label: string; colour: string; what: string }

/**
 * How the window's closed work turned out, as three slices of one bar. Read off each
 * closed line's own outcome, which is one of four mutually exclusive kinds, so the
 * slices always sum to `counts.closed`. The `movedOrSlipped` count cannot be used here:
 * it spans open overdue work as well, and a bar drawn from it would show closed work
 * that was never closed.
 */
export function outcomeSegments(record: RecordView): OutcomeSegment[] {
  const closed = record.lines.filter((l) => l.status === "closed");
  const n = (...kinds: OutcomeKind[]) => closed.filter((l) => kinds.includes(l.outcome.kind)).length;
  return [
    { key: "on", n: n("closed-on-date", "closed-early"), label: "on the first date", colour: "var(--sprout)", what: "Closed on the date first accepted, or before it, with no move in between." },
    { key: "moved", n: n("moved-closed"), label: "moved, then closed", colour: "var(--amber)", what: "The date changed after it was accepted, and it closed on the new one. Moving with a note is fine; the count is here so it is visible." },
    { key: "late", n: n("closed-late"), label: "closed late", colour: "var(--bad)", what: "Closed after the date it was accepted for, with no move in between." },
  ];
}

/** A day on the week-ahead chart: what is due on it, and whether anything on it is late. */
export interface DueDay { date: string; n: number; late: boolean }

/**
 * Open work and cadences due between today and a week out, one count per day. Anything
 * already past its date counts on today, because today is when it needs doing; no trend
 * is drawn, because the record holds months rather than years.
 */
export function dueWeek(record: RecordView): DueDay[] {
  const today = dateOf(record.asOf);
  // Every open line, not `working`: that set is ranked, capped at twenty for the web,
  // and holds candidates, none of which belongs on a chart of what is due. Open
  // cadences are already in `lines`, so this counts them once.
  const live = record.lines.filter((l) => l.status === "open");
  const out: DueDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = addCalendarDays(today, i);
    out.push({ date, n: live.filter((l) => l.dueDate === date).length, late: false });
  }
  const overdue = live.filter((l) => l.dueDate < today).length;
  if (overdue && out[0]) out[0] = { ...out[0], n: out[0].n + overdue, late: true };
  return out;
}

// ------------------------------------------------------------------ what else is on the owner

/** A unit the owner is the named lead of, with the work under it. */
export interface RecordUnitLine {
  programId: ProgramId;
  programName?: string;
  unitId: string;
  unitName: string;
  kind: string;
  /** Distinct people on the unit and everything under it. */
  people: number;
  /** Confirmed open work on the unit and everything under it. */
  openWork: number;
  /** Items under it nobody has answered for longer than the programme allows. */
  unattendedCount: number;
  clin?: ClinTag;
}

/** Somebody who reports to the owner, and what is on their plate across the owner's programs. */
export interface RecordReportLine {
  upn: PersonId;
  name: string;
  openWork: number;
  unattendedCount: number;
  /** The units they are currently on, named, for the sub-line. */
  units: string[];
}

/** One unanswered item on a unit the owner leads. The source stays with its owner. */
export interface RecordUnattendedLine {
  id: string;
  title: string;
  ownerUpn: PersonId;
  owner: string;
  programId: ProgramId;
  unitId: string;
  unitName: string;
  /** True when the item carries no unit of its own and was placed by its owner's membership. */
  viaOwner?: boolean;
  days: number;
  since: Iso;
}

export interface RecordSide {
  units: RecordUnitLine[];
  reports: RecordReportLine[];
  unattended: RecordUnattendedLine[];
}

export interface RecordSideInput {
  owner: PersonId;
  asOf?: Iso;
  people?: { upn: PersonId; displayName: string; managerUpn?: string | null }[];
  programs?: { id: ProgramId; name: string }[];
  /** Every active unit on the programs the owner is on. */
  units?: RecordUnit[];
  unitRoles?: { unitId: string; roleId: string; upn: PersonId }[];
  unitMembers?: { unitId: string; upn: PersonId; until: string | null }[];
  /**
   * The unit roles that carry accountability. The caller passes them rather than this module
   * importing them: `program-view` already imports `record`, and reaching back for the constant
   * would close a cycle that holds only while neither side reads the other at module load.
   */
  accountableRoles: readonly string[];
  /** Every commitment on those programs, in any status. */
  commitments: Commitment[];
  events: Audit[];
  /** The `unattended-after` duration per program; anything missing falls back to the default. */
  unattendedAfter?: Record<ProgramId, string | undefined>;
}

/**
 * What the owner is accountable for beside their own lines: the units they lead, the people who
 * report to them, and the work on those units nobody has answered.
 *
 * Unattended work is here because it is work on a contract and the person accountable for the unit
 * is entitled to see it (docs/2026-09-24-unconfirmed-work-is-visible.md). What is shown is the item,
 * its owner and how long it has sat, never the source it came from, which stays the owner's. Only
 * units the owner holds an accountable role on are counted, so this is never a view over somebody
 * else's team, and the lists come back in tree and roster order because the model forbids a ranking.
 */
export function recordSideboard(input: RecordSideInput): RecordSide {
  const asOfDate = new Date(input.asOf ?? new Date().toISOString());
  const units = (input.units ?? []).filter((u) => u.active !== false);
  const byId = new Map(units.map((u) => [u.id, u]));
  const clinFor = clinIndex(units);
  const programNames = new Map((input.programs ?? []).map((p) => [p.id, p.name]));
  const names = new Map((input.people ?? []).map((p) => [p.upn, p.displayName]));
  const members = (input.unitMembers ?? []).filter((m) => !m.until);

  const children = new Map<string, string[]>();
  for (const u of units) {
    if (!u.parentId) continue;
    children.set(u.parentId, [...(children.get(u.parentId) ?? []), u.id]);
  }
  const subtree = (id: string): Set<string> => {
    const out = new Set<string>();
    const walk = (at: string, depth: number) => {
      if (out.has(at) || depth > MAX_TREE_DEPTH) return;
      out.add(at);
      for (const k of children.get(at) ?? []) walk(k, depth + 1);
    };
    walk(id, 0);
    return out;
  };

  // A candidate carries no unit: `unitId` is null until somebody confirms it. Attributing
  // unanswered work by the commitment's own unit therefore finds none of it, so fall back
  // to where its owner sits, exactly as the unit's own week does.
  const unitsOfPerson = new Map<PersonId, Set<string>>();
  for (const m of members) unitsOfPerson.set(m.upn, (unitsOfPerson.get(m.upn) ?? new Set()).add(m.unitId));
  const onSubtree = (c: Commitment, sub: Set<string>) =>
    c.unitId ? sub.has(c.unitId) : [...(unitsOfPerson.get(c.owner) ?? [])].some((id) => sub.has(id));

  const accountable = new Set((input.unitRoles ?? [])
    .filter((r) => r.upn === input.owner && input.accountableRoles.includes(r.roleId))
    .map((r) => r.unitId));
  const led = units
    .filter((u) => (u.kind === "workstream" || u.kind === "team") && accountable.has(u.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const unitLines: RecordUnitLine[] = [];
  const waiting: RecordUnattendedLine[] = [];
  const seen = new Set<string>();
  for (const u of led) {
    const sub = subtree(u.id);
    const afterMs = thresholdMs(input.unattendedAfter?.[u.programId]);
    const here = unattended(input.commitments.filter((c) => c.programId === u.programId && onSubtree(c, sub)), input.events, asOfDate, afterMs);
    for (const w of here) {
      if (seen.has(w.commitment.id)) continue;
      seen.add(w.commitment.id);
      const on = w.commitment.unitId ?? u.id;
      waiting.push({
        id: w.commitment.id, title: w.commitment.title, ownerUpn: w.commitment.owner, owner: names.get(w.commitment.owner) ?? w.commitment.owner,
        programId: u.programId, unitId: on, unitName: byId.get(on)?.name ?? u.name,
        ...(w.commitment.unitId ? {} : { viaOwner: true }),
        days: w.days, since: w.since,
      });
    }
    unitLines.push({
      programId: u.programId, unitId: u.id, unitName: u.name, kind: u.kind,
      ...(programNames.has(u.programId) ? { programName: programNames.get(u.programId)! } : {}),
      people: new Set(members.filter((m) => sub.has(m.unitId)).map((m) => m.upn)).size,
      openWork: input.commitments.filter((c) => c.status === "open" && c.unitId && sub.has(c.unitId)).length,
      unattendedCount: here.length,
      ...(clinFor(u.id) ? { clin: clinFor(u.id)! } : {}),
    });
  }
  waiting.sort((a, b) => b.days - a.days || a.title.localeCompare(b.title));

  // A manager's authority over a report's work is the manager relation itself, not a unit role:
  // `visibility()` answers "record" on that person's confirmed and unconfirmed work alike. These
  // counts are narrower than that entitlement, because they span only the owner's own programs.
  const reports: RecordReportLine[] = (input.people ?? [])
    .filter((p) => p.managerUpn === input.owner)
    .map((p) => {
      const theirs = input.commitments.filter((c) => c.owner === p.upn);
      const byProgram = new Map<ProgramId, Commitment[]>();
      for (const c of theirs) byProgram.set(c.programId, [...(byProgram.get(c.programId) ?? []), c]);
      return {
        upn: p.upn, name: p.displayName,
        openWork: theirs.filter((c) => c.status === "open").length,
        unattendedCount: [...byProgram].reduce((n, [programId, cs]) => n + unattended(cs, input.events, asOfDate, thresholdMs(input.unattendedAfter?.[programId])).length, 0),
        units: [...new Set(members.filter((m) => m.upn === p.upn).map((m) => byId.get(m.unitId)?.name).filter((n): n is string => !!n))].sort(),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return { units: unitLines, reports, unattended: waiting };
}
